import './index.scss';

import PropUpPage from 'Components/PopUpPage';
import { actions } from 'core/redux/features/products';
import { selectFilteredProductIds } from 'core/redux/features/products/reducers/products';
import { IProduct } from 'core/redux/features/products/types';
import { selectGeoAddressOfActiveArea } from 'core/redux/features/shared/selectors';
import { ProductId } from 'core/redux/features/shared/types';
import { AppState } from 'core/redux/reducers';
import { locationParser } from 'core/utils/location';
import { closest } from 'dom-helpers';
import GoogleMap from 'google-map-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import MapBord, { PlainMapBoard } from '../Components/MapBord';
import { MapMarker } from '../Components/MapMarker';
import { useGeoPosition, useSelectViewModes } from '../hooks';

function useMapParams(product: IProduct | null) {
  const location = useLocation();
  const mapCenter = useSelector(selectGeoAddressOfActiveArea);

  function getDefaultPrams() {
    const searchParam = new URLSearchParams(location.search);
    const lat = Number(searchParam.get('lat')) || mapCenter.lat;
    const lng = Number(searchParam.get('lng')) || mapCenter.lng;

    return {
      lat,
      lng,
      zoom_level: 11,
    };
  }
  const [mapParams, setMapParams] = useState(getDefaultPrams);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const searchParam = new URLSearchParams(location.search);
    const lat = Number(searchParam.get('lat'));
    const lng = Number(searchParam.get('lng'));

    if (lat && lng) {
      setMapParams({
        ...mapParams,
        lat,
        lng,
      });
    }
    // eslint-disable-next-line
  }, [location]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!product) {
      return;
    }

    const lat = Number(product.geo.lat);
    const lng = Number(product.geo.lng);
    if (mapParams.lat !== lat && mapParams.lng !== lng) {
      setMapParams({
        ...mapParams,
        lat,
        lng,
      });
    }
    // eslint-disable-next-line
  }, [product]);

  return mapParams;
}

export default function ProductMap({
  onSelectProduct,
}: {
  onSelectProduct: (e: React.SyntheticEvent, productId: ProductId) => void;
}) {
  const userPos = useGeoPosition();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const { local, productIds, products, deviceType, isViewingProductInfo } =
    useSelector((state: AppState) => ({
      productIds: selectFilteredProductIds(state),
      products: state.products.entities.products, // all products
      loading: state.products.loading,
      local: state.local,
      deviceType: state.deviceType,
      isViewingProductInfo: state.products.viewInfo,
    }));

  const { viewMode } = useSelectViewModes();

  const [activeMapBoard, setActiveMapBoard] = useState<IProduct>(null);
  const mapParams = useMapParams(activeMapBoard);
  const [showUserBoard, setShowUserBoard] = useState(false);

  const handleViewProductInfo = useCallback(
    (e: React.SyntheticEvent, product: IProduct) => {
      onSelectProduct(e, product.id);
      if (isViewingProductInfo) {
        dispatch(
          actions.viewInfo({
            id: product.id,
            slug: product.slug,
          }),
        );
      }

      if (deviceType === 'mobile') {
        history.push(product.route);
      }
    },
    [deviceType, dispatch, history, isViewingProductInfo, onSelectProduct],
  );

  const handleOpenMapBoard = useCallback(
    (productId, { product, type }) => {
      if (type === 'guest' && userPos) {
        setShowUserBoard(!showUserBoard);
        return;
      }

      if (!activeMapBoard || activeMapBoard.id !== product.id) {
        setActiveMapBoard(product);
        dispatch(
          actions.activateProduct({
            id: productId,
            slug: product.slug,
          }),
        );
        if (isViewingProductInfo) {
          // dispatch(
          //   actions.viewInfo({
          //     id: product.id,
          //     slug: product.slug,
          //   })
          // )

          history.push(
            locationParser(location)
              .setPathName(product.route)
              .setHash(viewMode.route)
              .toString(),
          );
        }
      } else {
        setActiveMapBoard(null);
      }
    },
    [
      activeMapBoard,
      dispatch,
      history,
      isViewingProductInfo,
      location,
      showUserBoard,
      userPos,
      viewMode.route,
    ],
  );

  const handleGoogleApiLoaded = g => {
    // disable POI info window
    const set = g.maps.InfoWindow.prototype.set;
    g.maps.InfoWindow.prototype.set = function (key, _val) {
      if (key === 'map') {
        return;
      }
      // eslint-disable-next-line prefer-rest-params
      set.apply(this, arguments);
    };
  };

  const handMapClick = e => {
    const isBoard = closest(e.event.target, '.map-board');
    const isMarker = closest(e.event.target, '.map-marker');
    if (!isBoard && !isMarker && activeMapBoard) {
      setActiveMapBoard(null);
    }
  };

  return (
    <PropUpPage
      data-testid="product.map"
      scrollableContent={false}
      id="popup-page-map"
      whiteBackdrop={true}
    >
      <div className="product-map-page">
        <GoogleMap
          onClick={handMapClick}
          bootstrapURLKeys={{
            key: 'AIzaSyCUkQNg2UAbrh9LOkUMizBXchUScj1X5Y0',
            language: local,
          }}
          onGoogleApiLoaded={handleGoogleApiLoaded}
          defaultZoom={mapParams.zoom_level}
          center={{
            lat: mapParams.lat,
            lng: mapParams.lng,
          }}
          hoverDistance={29}
          debounced={true}
          options={{
            zoomControl: true,
            scaleControl: false,
            // disableDoubleClickZoom: true,
            scrollwheel: false,
            fullscreenControl: false,
            gestureHandling: 'greedy',
          }}
          margin={[20, 20, 20, 20]}
          yesIWantToUseGoogleMapApiInternals={true}
          onChildClick={handleOpenMapBoard}
        >
          {productIds.map(info => {
            const product = products[info.id];
            if (info.result.indexOf('status') > -1) {
              return null;
            }
            return (
              <MapMarker
                type="product"
                active={info.id === activeMapBoard?.id}
                product={product}
                filterResult={info.result}
                key={info.id}
                {...product.geo}
              />
            );
          })}

          {userPos !== null ? (
            <MapMarker
              active={showUserBoard}
              type="guest"
              lat={userPos[0]}
              lng={userPos[1]}
            />
          ) : null}

          {activeMapBoard && deviceType !== 'mobile' ? (
            <MapBord
              onClick={handleViewProductInfo}
              product={activeMapBoard}
              lat={Number(activeMapBoard.geo.lat)}
              lng={Number(activeMapBoard.geo.lng)}
            />
          ) : null}

          {showUserBoard ? (
            <PlainMapBoard
              title="Din nuværende placering"
              className="map-board--guest"
              lat={Number(userPos[0])}
              lng={Number(userPos[1])}
            />
          ) : null}
        </GoogleMap>
        {activeMapBoard && deviceType === 'mobile' ? (
          <div className="map-board-wrap">
            <MapBord
              onClick={handleViewProductInfo}
              product={activeMapBoard}
              className="map-board--mobile"
              lat={Number(activeMapBoard.geo.lat)}
              lng={Number(activeMapBoard.geo.lng)}
            />
          </div>
        ) : null}
      </div>
    </PropUpPage>
  );
}
